import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { Box, Icon, Link, Text, Title } from '@nimbus-ds/components';
import { ChevronLeftIcon } from '@nimbus-ds/icons';
import { navigateHeader } from '@tiendanube/nexo';
import nexo from 'App/nexoClient';
import { useIsMobile } from 'commons/hooks';

export interface HeaderInterface {
  title: string;
  subtitle?: string;
  subtitleItems?: React.ReactNode;
  backLinkPath?: string;
  backLinkText?: string;
  headerItems?: React.ReactNode;
  navBarItems?: React.ReactNode;
  hideBackButton?: boolean;
  skeleton?: boolean;
}

function Header({
  title,
  subtitle,
  subtitleItems,
  backLinkPath = 'back',
  backLinkText,
  headerItems,
  navBarItems,
  hideBackButton,
  skeleton,
}: HeaderInterface): JSX.Element {
  const isMobile = useIsMobile();
  const { goBack, push } = useHistory();

  useEffect(() => {
    if (skeleton) return;
    navigateHeader(nexo, {
      text: backLinkText,
      goTo: backLinkPath,
    });
  }, [backLinkPath, backLinkText, skeleton]);

  const renderBackButton = () => (
    <Link
      as="a"
      onClick={() => {
        if (backLinkPath !== 'back') push(backLinkPath);
        else goBack();
      }}
      textDecoration="none"
    >
      <Icon color="currentColor" source={<ChevronLeftIcon />} />
      <Text color="primary-textLow" fontWeight="bold">
        {backLinkText}
      </Text>
    </Link>
  );

  const MobileNavBar = (
    <Box
      display="flex"
      gap="4"
      justifyContent={hideBackButton ? 'flex-end' : 'space-between'}
    >
      {!hideBackButton && renderBackButton()}
      {navBarItems && <Box>{navBarItems}</Box>}
    </Box>
  );

  const HeaderBody = (
    <Box display="flex" gap="4" flexDirection="column" alignItems="stretch">
      <Box display="flex" gap="4" justifyContent="space-between">
        {skeleton ? (
          <Title.Skeleton as="h1" width="162px" />
        ) : (
          <Title as="h1">{title}</Title>
        )}
        {headerItems && <Box>{headerItems}</Box>}
      </Box>
      {subtitle && <Text>{subtitle}</Text>}
      {subtitleItems && (
        <Box display="flex" gap="4">
          {subtitleItems}
        </Box>
      )}
    </Box>
  );

  return (
    <Box display="flex" flexDirection="column" alignItems="stretch" gap="4">
      {isMobile && MobileNavBar}
      {HeaderBody}
    </Box>
  );
}

export default Header;
